import React, { useEffect, useCallback, useState } from 'react'
import { BText, BTitle, BLink } from '@prague-digi/ui-components'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { ReduxState } from 'app-types'
import classNames from '@prague-digi/class-names'

import { formatAccountNumber } from '../../utils/helpers'
import messages from '../../i18n'
import { Payment } from '../../redux/reducers/payment/model' // todo JMA move to app-types
import { modalDialogActions, paymentActions, paymentSelectors } from '../../redux'
import { unixTimeToCzDate } from '../../utils/dates'
import { intl, IntlInterface, wizard, WizardInterface } from '../../hoc'
import styles from './styles.scss'
import { ApiErrorInfo } from '../../api/api'
import { API_ERROR } from '../../constants/modals'
import { PageControlButtons, Notification } from '../../components'
import { HorizontalStepper, TableRow } from './components'

const msg = messages.pennyPaymentConfirm

interface MapDispatchToProps {
  fetchPennyPayment: Function
  pennyPaymentConfirm: Function
  showModalDialog: Function
}

interface MapStateToProps extends Payment {}

type Props = WizardInterface & IntlInterface & MapDispatchToProps & MapStateToProps

export const PennyPaymentConfirm = ({
  amount,
  currentAccountNumber,
  dueDate,
  fetchPennyPayment,
  originalAccountNumber,
  pennyPaymentConfirm,
  qrCode,
  showModalDialog,
  transactionNote,
  translate,
  formatCzechCurrency,
  translateHTML,
  wizard,
}: Props) => {
  const onEffect = useCallback(() => {
    fetchPennyPayment({
      error: (errorInfo: ApiErrorInfo) => {
        showModalDialog(API_ERROR({ errorInfo, onRetry: onEffect }))
      },
    })
  }, [fetchPennyPayment, showModalDialog])

  useEffect(() => {
    onEffect()
  }, [onEffect])

  const confirmPayment = () => {
    pennyPaymentConfirm({
      success: () => {
        wizard.gotoNext()
      },
      error: (errorInfo: ApiErrorInfo) => {
        showModalDialog(API_ERROR({ errorInfo, onRetry: confirmPayment }))
      },
    })
  }

  const [isQRPayment, handlePaymentMethod] = useState(true)

  const setQRPayment = useCallback(() => {
    handlePaymentMethod(true)
  }, [handlePaymentMethod])

  const setManualPayment = useCallback(() => {
    handlePaymentMethod(false)
  }, [handlePaymentMethod])

  const title = isQRPayment ? msg.qr.title : msg.manual.title

  return (
    <>
      <BTitle type="h3" theme={styles.title}>
        {translateHTML(title)}
      </BTitle>
      <BText theme="mb-0 pb-0">{translate(msg.info, { amount })}</BText>
      <BText>
        <b>{translate(msg.instant)}</b>
      </BText>

      <div className={styles.box}>
        <div className={styles.navBar}>
          <div
            className={classNames({ [styles.active]: isQRPayment })}
            role="button"
            tabIndex={0}
            onClick={setQRPayment}>
            {translate(msg.qr.tab)}
          </div>
          <div
            className={classNames({ [styles.active]: !isQRPayment })}
            role="button"
            tabIndex={0}
            onClick={setManualPayment}>
            {translate(msg.manual.tab)}
          </div>
        </div>

        <div className={classNames(styles.content)}>
          {isQRPayment ? (
            <>
              <div className={styles.qrCode}>
                <img alt="qr code" src={`data:image/png;base64,${qrCode}`} />
              </div>

              <HorizontalStepper />

              <BText center type="secondary" theme="mt-3 mb-0 pb-0">
                {translate(msg.qr.doesntWork.text)}{' '}
                <BLink onClick={setManualPayment}>{translate(msg.qr.doesntWork.link)}</BLink>
              </BText>

              <Notification
                description={translateHTML(msg.qr.warning, {
                  accountNumber: formatAccountNumber(originalAccountNumber),
                })}
                isMarginTop
              />
            </>
          ) : (
            <>
              <TableRow
                description={translate(msg.manual.fromAccount)}
                value={formatAccountNumber(originalAccountNumber)}
              />
              <TableRow
                description={translate(msg.manual.toAccount)}
                textToClipboard={formatAccountNumber(currentAccountNumber)}
                value={<b>{formatAccountNumber(currentAccountNumber)}</b>}
              />
              <TableRow
                description={translate(msg.manual.transactionNote)}
                textToClipboard={transactionNote}
                value={<b>{transactionNote}</b>}
              />
              <TableRow
                description={translate(msg.manual.amount)}
                value={formatCzechCurrency(amount)}
              />
              <TableRow
                description={translate(msg.manual.dueDate)}
                value={unixTimeToCzDate(dueDate)}
              />

              <Notification
                description={translateHTML(msg.manual.warning, {
                  accountNumber: formatAccountNumber(originalAccountNumber),
                })}
                isMarginTop
              />
            </>
          )}
        </div>
      </div>

      <PageControlButtons
        className={styles.controlButtons}
        onSubmit={confirmPayment}
        submitMessage={msg.continue}
      />
    </>
  )
}

const mapDispatchToProps: MapDispatchToProps = {
  fetchPennyPayment: paymentActions.fetchPennyPayment,
  pennyPaymentConfirm: paymentActions.pennyPaymentConfirm,
  showModalDialog: modalDialogActions.showModalDialog,
}

const mapStateToPros = (state: ReduxState): MapStateToProps => ({
  ...paymentSelectors.getPaymentDetails(state),
})

const enhance = compose(intl, wizard, connect(mapStateToPros, mapDispatchToProps))

export default enhance(PennyPaymentConfirm)
